/**
 * Issues API Service
 */

import { api } from './api';
import type { IssueSeverity } from '@/types/domain';

export interface IssuesListParams {
  project?: string | string[];
  scenario?: string | string[];
  status?: string | string[];
  severity?: string | string[];
  tester?: string | string[];
  search?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
  cursor?: string;
}

export interface IssuesListResponse {
  issues: Issue[];
  count: number;
  pagination: {
    page: number;
    page_size: number;
    total: number | null;
    total_pages: number | null;
    has_more: boolean;
    next_cursor: string | null;
  };
  filter_options: {
    projects: Array<{ id: string; name: string }>;
    scenarios: Array<{ id: string; name: string }>;
    severities: string[];
    testers: string[];
    statuses: string[];
  };
}

export interface GetScreenshotUploadUrlRequest {
  filename: string;
  size: number;
  content_type: string;
}

export interface GetScreenshotUploadUrlResponse {
  upload_url: string;
  s3_key: string;
}

export interface CreateIssueRequest {
  test_case_id: string;
  test_case_user_id?: string;
  title: string;
  description: string;
  severity: IssueSeverity;
  screenshot_s3_key?: string;
  sharepoint_file_url?: string;
}

export interface Issue {
  id: string;
  test_case_id: string;
  scenario_id?: string;
  project_id?: string;
  title: string;
  description: string;
  severity: string;
  status: string;
  project_number?: string;
  testscript_id?: string;
  scenario_name?: string;
  sharepoint_id?: string;
  sharepoint_web_url?: string;
  sharepoint_synced?: boolean;
  sharepoint_fields_skipped?: string[];
  sharepoint_rest_fields_failed?: string[];
  sharepoint_rest_error_detail?: string;
  sharepoint_error?: string;
  created_by?: string;
  created_at: string;
  updated_at?: string;
}

export const issuesApi = {
  /**
   * Get presigned URL for screenshot upload
   */
  getScreenshotUploadUrl: async (
    request: GetScreenshotUploadUrlRequest
  ): Promise<GetScreenshotUploadUrlResponse> => {
    return api.post<GetScreenshotUploadUrlResponse>(
      '/issues/screenshot-upload',
      request
    );
  },

  /**
   * Upload screenshot file to S3 using presigned URL
   */
  uploadScreenshot: async (presignedUrl: string, file: File): Promise<void> => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Screenshot upload failed: ${response.statusText}`);
    }
  },

  /**
   * Create new issue with optional screenshot
   *
   * Workflow:
   * 1. If screenshot provided:
   *    a. Get presigned URL
   *    b. Upload file to S3
   *    c. Include S3 key in issue creation
   * 2. Create issue with screenshot reference
   */
  create: async (request: CreateIssueRequest): Promise<Issue> => {
    return api.post<Issue>('/issues', request);
  },

  /**
   * Get issue by ID
   */
  getById: async (issueId: string): Promise<Issue> => {
    return api.get<Issue>(`/issues/${issueId}`);
  },

  /**
   * List issues for test case
   */
  listByTestCase: async (testCaseId: string): Promise<Issue[]> => {
    return api.get<Issue[]>(`/issues?test_case_id=${testCaseId}`);
  },

  /**
   * List issues for project
   */
  listByProject: async (projectId: string): Promise<Issue[]> => {
    return api.get<Issue[]>(`/issues?project_id=${projectId}`);
  },

  /**
   * Admin-facing filterable list of issues (GET /admin/issues).
   *
   * Supports multi-value filters (project, scenario, status, severity, tester),
   * a substring search, and date-range bounds on created_at. Pagination uses
   * page/page_size by default; pass `cursor` for cursor-based continuation.
   *
   * Array filters serialize as repeated keys (?project=A&project=B) so that
   * individual values containing commas survive round-trip intact.
   */
  list: async (params?: IssuesListParams): Promise<IssuesListResponse> => {
    const qs = new URLSearchParams();
    const appendMulti = (key: string, v: string | string[] | undefined): void => {
      if (v == null) return;
      if (Array.isArray(v)) {
        for (const item of v) qs.append(key, item);
      } else {
        qs.set(key, v);
      }
    };

    appendMulti('project', params?.project);
    appendMulti('scenario', params?.scenario);
    appendMulti('status', params?.status);
    appendMulti('severity', params?.severity);
    appendMulti('tester', params?.tester);
    if (params?.search) qs.set('search', params.search);
    if (params?.date_from) qs.set('date_from', params.date_from);
    if (params?.date_to) qs.set('date_to', params.date_to);
    if (params?.page != null) qs.set('page', params.page.toString());
    if (params?.page_size != null) qs.set('page_size', params.page_size.toString());
    if (params?.cursor) qs.set('cursor', params.cursor);

    const query = qs.toString();
    return api.get<IssuesListResponse>(
      query ? `/admin/issues?${query}` : '/admin/issues',
    );
  },
};
